type SubjectNameLookup = Map<string, string> | Record<string, string>;

type BulkCreateUpdateErrorOptions = {
  submittedSubjectIds?: string[];
  subjectNameById?: SubjectNameLookup;
};

const lookupSubjectName = (
  subjectId: string,
  subjectNameById?: SubjectNameLookup,
) => {
  if (!subjectNameById) return undefined;
  if (subjectNameById instanceof Map) return subjectNameById.get(subjectId);
  return subjectNameById[subjectId];
};

const formatValue = (value: any): string => {
  if (Array.isArray(value)) {
    return value
      .map((entry: any) =>
        typeof entry === "object" && entry !== null
          ? JSON.stringify(entry)
          : String(entry),
      )
      .join("; ");
  }
  if (typeof value === "object" && value !== null) return JSON.stringify(value);
  return String(value);
};

const formatErrorEntry = (key: string, value: any): string => {
  if (value?.value && Array.isArray(value.value)) {
    return `${key}: ${value.value
      .map((entry: any) => (typeof entry === "string" ? entry : String(entry)))
      .join("; ")}`;
  }
  return `${key}: ${formatValue(value)}`;
};

const formatBulkErrorDetail = (error: any): string => {
  if (!error) return "";
  if (typeof error === "string") return error;
  if (Array.isArray(error)) return formatValue(error);
  if (typeof error === "object") {
    const entries = Object.entries(error);
    if (entries.length === 0) return "";
    return entries.map(([key, value]) => formatErrorEntry(key, value)).join("\n");
  }
  return String(error);
};

const getFailedResultErrors = (result: any) => {
  if (result?.errors) return result.errors;
  const anchorDataValues = result?.anchor_data_values;
  if (anchorDataValues && typeof anchorDataValues === "object") {
    const errorsByTemplate = Object.entries(anchorDataValues).reduce(
      (acc, [templateName, templateResult]) => {
        if (
          templateResult &&
          typeof templateResult === "object" &&
          "errors" in templateResult
        ) {
          acc[templateName] = (templateResult as any).errors;
        }
        return acc;
      },
      {} as Record<string, any>,
    );
    if (Object.keys(errorsByTemplate).length > 0) return errorsByTemplate;
  }
  return result;
};

export const getAnchorDataBulkCreateUpdateErrors = (
  response: any,
  options: BulkCreateUpdateErrorOptions = {},
): string[] => {
  const responseData = response?.data ?? {};
  const resultRows = Array.isArray(responseData.results)
    ? responseData.results
    : [];
  if (resultRows.length === 0) return [];

  const failedResults = resultRows.filter(
    (result: any) => result?.status !== "success",
  );
  const successfulSubjectIds = resultRows
    .filter((result: any) => result?.status === "success" && result?.subject)
    .map((result: any) => String(result.subject));
  const failedSubjectIds = new Set<string>(
    failedResults
      .map((result: any) => result?.subject)
      .filter(Boolean)
      .map(String),
  );
  const missingResultSubjectIds = (options.submittedSubjectIds ?? []).filter(
    (subjectId) =>
      !successfulSubjectIds.includes(subjectId) &&
      !failedSubjectIds.has(subjectId),
  );

  return [
    ...failedResults.map((result: any) => {
      const subjectId = result?.subject ? String(result.subject) : "";
      const subjectName =
        result?.subject_name ??
        (subjectId
          ? lookupSubjectName(subjectId, options.subjectNameById)
          : undefined) ??
        (subjectId || "Unknown subject");
      return `${subjectName}:\n${formatBulkErrorDetail(
        getFailedResultErrors(result),
      )}`;
    }),
    ...missingResultSubjectIds.map((subjectId) => {
      const subjectName =
        lookupSubjectName(subjectId, options.subjectNameById) ?? subjectId;
      return `${subjectName}:\nNo result returned for subject`;
    }),
  ];
};
